import { createSlice } from "@reduxjs/toolkit";
import { fetchPatientPendingList } from "../../apis/dispense/patientPendingList";
import {
	markPatientPrescriptionsPharmacistSigned,
	patchPatientPrescriptionsPharmacistSignedState,
} from "./pharmacistPrescriptionSignature";

const initialState = {
	loading: false,
	data: [],
	error: null,
	currentPage: 1,
	totalPages: null,
	currentRequestKey: null
}

const patientPendingListSlice = createSlice({
	name: 'patientPendingList',
	initialState,

	reducers: {
		removePatientById(state, action) {
			const patientIdToRemove = action.payload;
			state.data = state.data.filter(item => item.id !== patientIdToRemove);
		},
		clearData(state) {
			state.data = []
		},
		pendingDispenseRemovingPageNumber(state) {
			state.currentPage = 1
		}
	},

	extraReducers: (builder) => {
		builder.addCase(fetchPatientPendingList.pending.type, (state, action) => {
			// Clear data when starting a new request (page 1) to prevent race conditions
			const isPageOne = action.meta?.arg?.page === 1;
			return {
				...state,
				loading: true,
				error: null,
				data: isPageOne ? [] : state.data, // Clear data only for new requests (page 1)
				currentRequestKey: isPageOne ? action.meta?.arg?.currentRequestKey : state.currentRequestKey
			}
		})
		builder.addCase(fetchPatientPendingList.fulfilled.type, (state, action) => {
			// If it's page 1, replace data (new filter or initial load)
			// Otherwise append for pagination
			const isPageOne = action.payload.meta.current_page === 1;
			const requestKey = action.meta?.arg?.currentRequestKey;
			
			// For page 1 requests, validate that the filter type matches
			// This prevents race conditions where an old request completes after a new one
			if (isPageOne) {
				// Only update if this response matches the current filter type (prevents stale responses)
				if (state.currentRequestKey !== null && requestKey !== state.currentRequestKey) {
					// This is a stale response from an old filter, ignore it
					return state;
				}
				
				// Replace data for new requests (filter change or initial load)
				return {
					...state,
					loading: false,
					data: action.payload.data || [],
					currentPage: action.payload.meta.current_page,
					totalPages: action.payload.meta.last_page,
					error: null,
					currentRequestKey: requestKey || null
				};
			} else {
				// Append data for pagination (only if filter type still matches)
				if (requestKey && requestKey !== state.currentRequestKey) {
					// Stale pagination request, ignore it
					return state;
				}
				
				const existingIds = new Set(state?.data?.map(item => item.id));
				const newData = action?.payload?.data?.filter(item => !existingIds.has(item.id));

				return {
					...state,
					loading: false,
					data: [...state.data, ...newData],
					currentPage: action.payload.meta.current_page,
					totalPages: action.payload.meta.last_page,
					error: null
				};
			}
		});

		builder.addCase(fetchPatientPendingList.rejected.type, (state, action) => {
			return {
				...state,
				loading: false,
				error: action.payload,
				data: []
			}
		})
		builder.addCase(patientPendingListSlice.actions.removePatientById, (state, action) => {
			const patientIdToRemove = action.payload;
			state.data = state.data.filter(item => item.patient_id !== patientIdToRemove);
		});
		builder.addCase(markPatientPrescriptionsPharmacistSigned, (state, action) => {
			patchPatientPrescriptionsPharmacistSignedState(state, action.payload);
		});
	}

})

export const { removePatientById, clearData, pendingDispenseRemovingPageNumber } = patientPendingListSlice.actions;

export default patientPendingListSlice.reducer;
